import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { formatDateTime as formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowsClockwise, EnvelopeSimple, CheckCircle, WarningCircle, Question, Spinner } from '@phosphor-icons/react';

const TYPE_LABELS = {
  signup: { label: 'Signup', color: 'bg-emerald-100 text-emerald-700' },
  password_reset: { label: 'Password Reset', color: 'bg-indigo-100 text-indigo-700' },
  odm_ack: { label: 'ODM Ack', color: 'bg-sky-100 text-sky-700' },
};

const STATUS_BADGE = {
  sent: { label: 'Sent', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: WarningCircle },
  no_recipient: { label: 'No matching user', color: 'bg-slate-100 text-slate-600', icon: Question },
  unconfigured: { label: 'Resend not configured', color: 'bg-amber-100 text-amber-700', icon: WarningCircle },
};

const StatCard = ({ label, value, color, testid }) => (
  <Card className="p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</p>
    <p className={`text-3xl font-bold tabular-nums ${color}`}>{value}</p>
  </Card>
);

export default function EmailAuditPage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ sent: 0, failed: 0, no_recipient: 0, unconfigured: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await api.get('/integrations/email/audit', { params });
      setLogs(res.data.logs || []);
      setSummary(res.data.summary || {});
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load email audit log');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(fetchLogs, 60000);
    return () => clearInterval(id);
  }, [fetchLogs]);

  return (
    <div className="space-y-6" data-testid="email-audit-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <EnvelopeSimple size={32} className="text-indigo-700" weight="duotone" />
            Email Audit Log
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Every transactional email sent by AZAI Billing — signup welcomes, password resets, and admin notifications.
          </p>
        </div>
        <Button
          onClick={fetchLogs}
          variant="outline"
          size="sm"
          disabled={loading}
          data-testid="email-audit-refresh"
          className="shrink-0"
        >
          {loading ? <Spinner size={14} className="animate-spin mr-2" /> : <ArrowsClockwise size={14} className="mr-2" />}
          Refresh
        </Button>
      </header>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={total} color="text-slate-900" testid="stat-total" />
        <StatCard label="Sent" value={summary.sent || 0} color="text-emerald-600" testid="stat-sent" />
        <StatCard label="Failed" value={summary.failed || 0} color="text-red-600" testid="stat-failed" />
        <StatCard label="No User" value={summary.no_recipient || 0} color="text-slate-500" testid="stat-no-recipient" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[200px]" data-testid="filter-type">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="signup">Signup welcome</SelectItem>
            <SelectItem value="password_reset">Password reset</SelectItem>
            <SelectItem value="odm_ack">ODM Ack</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[200px]" data-testid="filter-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="no_recipient">No user matched</SelectItem>
            <SelectItem value="unconfigured">Resend unconfigured</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Log table */}
      <Card className="overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="flex justify-center py-16" data-testid="email-audit-loading">
            <Spinner size={32} className="animate-spin text-indigo-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center" data-testid="email-audit-empty">
            <EnvelopeSimple size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-700">No emails yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Welcome and password-reset emails will appear here as they're sent.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-audit-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left font-medium text-slate-600 px-4 py-3">Type</th>
                  <th className="text-left font-medium text-slate-600 px-4 py-3">Recipient</th>
                  <th className="text-left font-medium text-slate-600 px-4 py-3">Subject / Label</th>
                  <th className="text-left font-medium text-slate-600 px-4 py-3">Sent At</th>
                  <th className="text-left font-medium text-slate-600 px-4 py-3">Status</th>
                  <th className="text-left font-medium text-slate-600 px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((log, idx) => {
                  const t = TYPE_LABELS[log.type] || { label: log.type, color: 'bg-slate-100 text-slate-700' };
                  const s = STATUS_BADGE[log.status] || { label: log.status, color: 'bg-slate-100 text-slate-700', icon: Question };
                  const StatusIcon = s.icon;
                  return (
                    <tr
                      key={`${log.type}-${log.sent_at}-${idx}`}
                      className="hover:bg-slate-50/50"
                      data-testid={`email-row-${idx}`}
                    >
                      <td className="px-4 py-3">
                        <Badge className={`text-[10px] ${t.color}`}>{t.label}</Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700 break-all max-w-xs">
                        {log.to || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{log.label}</td>
                      <td className="px-4 py-3 text-slate-500 tabular-nums whitespace-nowrap">
                        {formatDate(log.sent_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`text-[10px] inline-flex items-center gap-1 ${s.color}`}>
                          <StatusIcon size={11} weight="bold" />
                          {s.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-md">
                        {log.error ? (
                          <span className="text-red-600 break-words" title={log.error}>
                            {log.error.slice(0, 120)}
                            {log.error.length > 120 ? '…' : ''}
                          </span>
                        ) : log.email_id ? (
                          <span className="font-mono text-slate-400 text-[10px]">id: {log.email_id}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-slate-400 text-center">
        Auto-refreshes every 60 seconds · Showing {logs.length} of {total} entries
      </p>
    </div>
  );
}
